// src/components/ApprovalDialogs.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, Typography, Alert, FormControl, InputLabel,
  Select, MenuItem, Card, CardContent, Stack, Chip, CircularProgress,
  Divider, Avatar, ListItemText, ListItemAvatar,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Public as PublicIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';

import { FleetVehicle } from '../types';
import { driverAPI } from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────
interface DriverOption {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  licenseNumber: string;
  status: string;
}

interface SupervisorApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: (comments: string) => void;
  requestDetails: {
    purpose: string;
    destination: string;
    requester: string;
    passengerCount: number;
  };
  loading?: boolean;
}

interface FleetApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: (vehicleId: string, driverName: string, driverPhone: string | null, comments: string) => void;
  requestDetails: {
    purpose: string;
    destination: string;
    requester: string;
    passengerCount: number;
    startDate: string;
    endDate: string;
    category?: string;
    // Public portal request fields
    requesterPhone?: string | null;
    requesterEmail?: string | null;
    referenceNumber?: string | null;
  };
  availableVehicles: FleetVehicle[];
  loading?: boolean;
}

interface RejectionDialogProps {
  open: boolean;
  onClose: () => void;
  onReject: (reason: string) => void;
  requestDetails: {
    purpose: string;
    requester: string;
  };
  loading?: boolean;
}


// ── Supervisor Approval Dialog ────────────────────────────────────────────────
export const SupervisorApprovalDialog: React.FC<SupervisorApprovalDialogProps> = ({
  open, onClose, onApprove, requestDetails, loading = false
}) => {
  const [comments, setComments] = useState('');

  const handleClose = () => { setComments(''); onClose(); };
  const handleApprove = () => { onApprove(comments || 'Approved'); setComments(''); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve Request</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Approving this will send it to the fleet manager for vehicle assignment.
          </Alert>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Request Details</Typography>
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Requester:</strong> {requestDetails.requester}</Typography>
                <Typography variant="body2"><strong>Purpose:</strong> {requestDetails.purpose}</Typography>
                <Typography variant="body2"><strong>Destination:</strong> {requestDetails.destination}</Typography>
                <Typography variant="body2"><strong>Passengers:</strong> {requestDetails.passengerCount}</Typography>
              </Stack>
            </CardContent>
          </Card>

          <TextField
            fullWidth multiline rows={3}
            label="Comments (Optional)"
            value={comments}
            onChange={e => setComments(e.target.value)}
            placeholder="Add any approval notes or instructions..."
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleApprove} variant="contained" color="success" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}>
          {loading ? 'Approving...' : 'Approve Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ── Fleet Approval Dialog ─────────────────────────────────────────────────────
export const FleetApprovalDialog: React.FC<FleetApprovalDialogProps> = ({
  open, onClose, onApprove, requestDetails, availableVehicles, loading = false
}) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedDriverId,  setSelectedDriverId]  = useState('');
  const [customDriver,      setCustomDriver]       = useState('');
  const [useCustomDriver,   setUseCustomDriver]    = useState(false);
  const [comments,          setComments]           = useState('');
  const [drivers,           setDrivers]            = useState<DriverOption[]>([]);
  const [driversLoading,    setDriversLoading]     = useState(false);

  // Load active drivers when dialog opens
  useEffect(() => {
    if (open) {
      loadDrivers();
      if (availableVehicles.length === 1) setSelectedVehicleId(availableVehicles[0].id);
    }
  }, [open]);

  const loadDrivers = async () => {
    setDriversLoading(true);
    try {
      const data = await driverAPI.getAllDrivers({ status: 'active' });
      setDrivers(data.map((d: any) => ({
        _id:           d._id,
        firstName:     d.firstName,
        lastName:      d.lastName,
        phone:         d.phone,
        licenseNumber: d.licenseNumber,
        status:        d.status,
      })));
    } catch (err) {
      console.error('Failed to load drivers:', err);
    } finally {
      setDriversLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedVehicleId('');
    setSelectedDriverId('');
    setCustomDriver('');
    setUseCustomDriver(false);
    setComments('');
  };

  const handleClose = () => { handleReset(); onClose(); };

  const handleApprove = () => {
    if (!selectedVehicleId) return;

    let driverName:  string      = 'TBD';
    let driverPhone: string|null = null;

    if (useCustomDriver && customDriver.trim()) {
      driverName  = customDriver.trim();
      driverPhone = null;
    } else if (selectedDriverId) {
      const d = drivers.find(x => x._id === selectedDriverId);
      if (d) {
        driverName  = `${d.firstName} ${d.lastName}`;
        driverPhone = d.phone || null;
      }
    }

    onApprove(selectedVehicleId, driverName, driverPhone, comments || 'Vehicle assigned');
    handleReset();
  };

  const selectedVehicle = availableVehicles.find(v => v.id === selectedVehicleId);
  const selectedDriver  = drivers.find(d => d._id === selectedDriverId);

  const isPortalRequest = !!(requestDetails.referenceNumber || requestDetails.requesterPhone);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Vehicle & Driver</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>

          {/* Request summary */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Request Details</Typography>
                {isPortalRequest && (
                  <Chip icon={<PublicIcon />} size="small" label="Portal Request" variant="outlined" color="secondary" />
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Requester:</strong> {requestDetails.requester}</Typography>
                <Typography variant="body2"><strong>Purpose:</strong> {requestDetails.purpose}</Typography>
                <Typography variant="body2"><strong>Destination:</strong> {requestDetails.destination}</Typography>
                <Typography variant="body2">
                  <strong>Period:</strong> {new Date(requestDetails.startDate).toLocaleString()} — {new Date(requestDetails.endDate).toLocaleString()}
                </Typography>
                <Typography variant="body2"><strong>Passengers:</strong> {requestDetails.passengerCount}</Typography>
              </Stack>

              {/* Contact info for portal requests */}
              {isPortalRequest && (requestDetails.requesterPhone || requestDetails.requesterEmail) && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Contact
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {requestDetails.requesterPhone && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          <a href={`tel:${requestDetails.requesterPhone}`} style={{ color: 'inherit' }}>
                            {requestDetails.requesterPhone}
                          </a>
                        </Typography>
                      </Stack>
                    )}
                    {requestDetails.requesterEmail && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{requestDetails.requesterEmail}</Typography>
                      </Stack>
                    )}
                    {requestDetails.referenceNumber && (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                        {requestDetails.referenceNumber}
                      </Typography>
                    )}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>

          {availableVehicles.length === 0 ? (
            <Alert severity="error" icon={<WarningIcon />}>
              No vehicles available for this time slot. Please reject or adjust the request.
            </Alert>
          ) : (
            <>
              {/* Vehicle selection */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Vehicle *</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Vehicle</InputLabel>
                <Select
                  value={selectedVehicleId}
                  onChange={e => setSelectedVehicleId(e.target.value)}
                  label="Select Vehicle"
                  disabled={loading}
                >
                  {availableVehicles.map(v => (
                    <MenuItem key={v.id} value={v.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <CarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {v.plateNumber} — {v.make} {v.model} ({v.year})
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                          <Chip size="small" label={v.category} variant="outlined" />
                          {v.seatingCapacity && (
                            <Chip size="small" label={`${v.seatingCapacity} seats`} variant="outlined" />
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedVehicle && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <strong>{selectedVehicle.plateNumber}</strong> — {selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Driver selection */}
              <Typography variant="subtitle2" gutterBottom>Driver</Typography>

              {!useCustomDriver ? (
                <>
                  <FormControl fullWidth sx={{ mb: 1 }} disabled={driversLoading || loading}>
                    <InputLabel>
                      {driversLoading ? 'Loading drivers...' : 'Select Driver'}
                    </InputLabel>
                    <Select
                      value={selectedDriverId}
                      onChange={e => setSelectedDriverId(e.target.value)}
                      label={driversLoading ? 'Loading drivers...' : 'Select Driver'}
                    >
                      <MenuItem value="">
                        <Typography color="text.secondary" variant="body2">— Assign later (TBD) —</Typography>
                      </MenuItem>
                      {drivers.map(d => (
                        <MenuItem key={d._id} value={d._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                              {d.firstName.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {d.firstName} {d.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {d.phone} · License: {d.licenseNumber}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedDriver && (
                    <Alert severity="info" sx={{ mb: 1 }} icon={<PersonIcon />}>
                      <strong>{selectedDriver.firstName} {selectedDriver.lastName}</strong>
                      {' · '}{selectedDriver.phone}
                    </Alert>
                  )}

                  <Button size="small" variant="text" onClick={() => setUseCustomDriver(true)} sx={{ mb: 2 }}>
                    Enter driver name manually instead
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Driver Name"
                    value={customDriver}
                    onChange={e => setCustomDriver(e.target.value)}
                    placeholder="Enter driver name"
                    disabled={loading}
                    sx={{ mb: 1 }}
                    InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} /> }}
                  />
                  <Button size="small" variant="text" onClick={() => { setUseCustomDriver(false); setCustomDriver(''); }} sx={{ mb: 2 }}>
                    ← Pick from driver list instead
                  </Button>
                </>
              )}

              <TextField
                fullWidth multiline rows={2}
                label="Comments (Optional)"
                value={comments}
                onChange={e => setComments(e.target.value)}
                placeholder="Add assignment notes or special instructions..."
                disabled={loading}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          color="success"
          disabled={loading || !selectedVehicleId || availableVehicles.length === 0}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Assigning...' : 'Assign Vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ── Rejection Dialog ──────────────────────────────────────────────────────────
export const RejectionDialog: React.FC<RejectionDialogProps> = ({
  open, onClose, onReject, requestDetails, loading = false
}) => {
  const [reason, setReason] = useState('');
  const handleClose  = () => { setReason(''); onClose(); };
  const handleReject = () => { if (!reason.trim()) return; onReject(reason); setReason(''); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject Request</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please provide a reason — this will be visible to the requester.
          </Alert>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Request Details</Typography>
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Requester:</strong> {requestDetails.requester}</Typography>
                <Typography variant="body2"><strong>Purpose:</strong> {requestDetails.purpose}</Typography>
              </Stack>
            </CardContent>
          </Card>

          <TextField
            fullWidth required multiline rows={4}
            label="Rejection Reason *"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Please explain why this request is being rejected..."
            disabled={loading}
            helperText="This reason will be shown to the requester"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleReject} variant="contained" color="error"
          disabled={loading || !reason.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}>
          {loading ? 'Rejecting...' : 'Reject Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};